import { redirect, type LoaderFunctionArgs } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import { useCityStore } from '@/stores/city'
import { useLeagueStore } from '@/stores/league'
import { isLegacyCityRouteId, normalizeCityId } from '@/utils/cityIds'
import { resolveDefaultLandingRoute } from '@/config/hubPersonalization'
import { resolveLocation } from './navigation'
import type { AppRoute } from './routes'

/**
 * Port of the legacy `router.beforeEach`. Runs as the loader of every leaf
 * route, so it executes before the page renders on each navigation to it.
 */

let isFirstNavigation = true
let appliedDefaultLanding = false

const go = (to: Parameters<typeof resolveLocation>[0]) => redirect(resolveLocation(to))

export function createGuard (route: AppRoute) {
  const meta = route.meta || {}
  const name = route.name

  return async ({ request, params }: LoaderFunctionArgs) => {
    const firstNavigation = isFirstNavigation
    isFirstNavigation = false

    if (meta.embed) return null

    const url = new URL(request.url)
    const fullPath = `${url.pathname}${url.search}${url.hash}`

    const auth = useAuthStore.getState()
    if (auth.loading) await auth.initAuth()

    const cityStore = useCityStore.getState()
    if (!cityStore.citiesLoaded) await cityStore.loadCities()

    const rawCityId = params.cityId
    if (rawCityId && isLegacyCityRouteId(rawCityId)) {
      const rest = url.pathname.replace(/^\/bridgewater\/?/, '')
      const suffix = rest ? `/${rest}` : ''
      throw redirect(`/southshore${suffix}${url.search}${url.hash}`)
    }

    if (meta.requiresCity && rawCityId) {
      const cityId = normalizeCityId(rawCityId)
      const city = useCityStore.getState().getCityById(cityId)
      if (!city) throw go({ name: 'CitySelect' })

      await useCityStore.getState().setCurrentCity(cityId)

      if (rawCityId !== cityId) {
        const fixed = url.pathname.replace(`/${rawCityId}`, `/${cityId}`)
        throw redirect(`${fixed}${url.search}${url.hash}`)
      }
    }

    const { user } = useAuthStore.getState()

    if (meta.requiresAuth && !user) {
      throw go({ name: 'Login', query: { redirect: fullPath } })
    }

    // Re-read after any awaits so role checks see the loaded profile.
    const a = useAuthStore.getState()

    if (meta.requiresAdmin) {
      const cityId = params.cityId
      if (!a.isCityAdmin(cityId) && !a.isSuperAdmin) {
        throw cityId ? go({ name: 'CityHome', params: { cityId } }) : go({ name: 'CitySelect' })
      }
    }

    if (meta.requiresLeagueAccess) {
      let hasAccess = a.hasLeagueAccess
      if (!hasAccess && a.user?.uid) {
        const leagueStore = useLeagueStore.getState()
        await leagueStore.loadCaptainTeams(a.user.uid)
        if (useLeagueStore.getState().teams.length > 0) {
          void useLeagueStore.getState().syncCaptainProfileFromTeams(a.user.uid)
          hasAccess = true
        }
      }
      if (!hasAccess) throw go({ name: 'CitySelect' })
    }

    if (meta.requiresSuperAdmin && !a.isSuperAdmin) {
      throw go({ name: 'CitySelect' })
    }

    if (meta.requiresLeagueAdminOf) {
      const leagueId = params.leagueId as string
      if (!a.isLeagueAdminOf(leagueId)) throw redirect(`/leagues/${leagueId}/home`)
    }

    if (meta.requiresTournamentConfig && !a.isTournamentAdminOf(params.tournamentId)) {
      throw go({ name: 'TournamentHub' })
    }

    if (meta.requiresTournamentOps) {
      const id = params.tournamentId
      if (!a.isTournamentAdminOf(id) && !a.isTournamentCaptainOf(id)) throw go({ name: 'TournamentHub' })
    }

    if (meta.requiresTrainingConfig && !a.isTrainingAdminOf(params.trainingSessionId)) {
      throw go({ name: 'TrainingHub' })
    }

    if (meta.requiresTrainingOps) {
      const id = params.trainingSessionId
      if (!a.isTrainingAdminOf(id) && !a.isTrainingCaptainOf(id)) throw go({ name: 'TrainingHub' })
    }

    if (!appliedDefaultLanding && name === 'CitySelect' && firstNavigation) {
      appliedDefaultLanding = true
      if (a.isAuthenticated && a.userProfile) {
        const landingPath = resolveDefaultLandingRoute(a.userProfile, { hasLeagueAccess: a.hasLeagueAccess })
        if (landingPath && landingPath !== fullPath) throw redirect(landingPath)
      }
    }

    return null
  }
}
